/**
 * RFC-0008 T4: 用户身份上下文（沿用 north-slide 旧 app 的协议）
 *
 * 状态机：
 * 1. iframe 加载后发 runtime:init / runtime:loading / runtime:running 三个生命周期消息
 *    （小北宿主只在收到 running 后才注册 getUserInfo 等 API）
 * 2. 通过 api_call:host 调 getUserInfo 拿 host_user_id（不可见，由宿主提供）
 * 3. POST /api/user (action=check) 查 D1 绑定
 *    - 已绑定 → ready
 *    - 未绑定 → unbound（显示注册卡片）
 * 4. 用户点击注册 → POST /api/user (action=register) → ready
 */
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';

export interface UserInfo {
  userId: string; // app_user_id（短 ID，对外展示用）
  isNew: boolean;
}

interface UserContextValue {
  user: UserInfo | null;
  status: 'loading' | 'ready' | 'unbound' | 'error';
  error: string | null;
  register: () => Promise<void>;
}

const UserContext = createContext<UserContextValue>({
  user: null,
  status: 'loading',
  error: null,
  register: async () => {},
});

export function useUser() {
  return useContext(UserContext);
}

function isInIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
}

/** 1. 触发宿主注册 API（必须在调用 getUserInfo 之前） */
function sendRuntimeMessages() {
  const send = (type: string) => {
    window.parent.postMessage({ type, payload: {} }, '*');
  };
  send('runtime:init');
  send('runtime:loading');
  send('runtime:running');
}

async function getHostUserId(): Promise<string> {
  sendRuntimeMessages();
  // 宿主侧可能要几个 tick 才注册完 API
  await new Promise((r) => setTimeout(r, 500));

  const maxAttempts = 20;
  const interval = 500;
  for (let i = 0; i < maxAttempts; i++) {
    try {
      if (i > 0) await new Promise((r) => setTimeout(r, interval));
      return await callGetUserInfoOnce();
    } catch (e) {
      const msg = (e as Error)?.message ?? '';
      if (msg.includes('not registered') && i < maxAttempts - 1) {
        console.log(
          `[decks] getUserInfo attempt ${i + 1} failed (not registered), retrying...`,
        );
        continue;
      }
      if (msg.includes('timeout') && i < maxAttempts - 1) {
        console.log(`[decks] getUserInfo attempt ${i + 1} timed out, retrying...`);
        continue;
      }
      if (i === maxAttempts - 1) throw e;
      throw e;
    }
  }
  throw new Error('getUserInfo failed after all retries');
}

function callGetUserInfoOnce(): Promise<string> {
  const mid = `${Date.now()}${Math.random().toString(36).slice(2)}`;
  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      window.removeEventListener('message', handler);
      reject(new Error('getUserInfo timeout'));
    }, 3000);

    function handler(event: MessageEvent) {
      const data = event.data;
      if (data?.type === 'api_call:response' && data?.messageId === mid) {
        window.removeEventListener('message', handler);
        clearTimeout(timeout);
        const payload = data.payload;
        if (payload?.success && payload?.result) {
          const r = payload.result;
          const hostId = r.user_id || r.uid || '';
          if (!hostId) reject(new Error('empty host_user_id'));
          else resolve(hostId);
        } else {
          reject(new Error(payload?.error || 'getUserInfo failed'));
        }
      }
    }

    window.addEventListener('message', handler);
    window.parent.postMessage(
      {
        type: 'api_call:host',
        source: 'north-app-decks',
        messageId: mid,
        payload: { apiName: 'getUserInfo', args: {} },
      },
      '*',
    );
  });
}

async function checkBinding(hostUserId: string): Promise<string | null> {
  const res = await fetch('/api/user', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ host_user_id: hostUserId, action: 'check' }),
  });
  if (!res.ok) throw new Error(`/api/user check failed: ${res.status}`);
  const data = (await res.json()) as { app_user_id?: string; exists: boolean };
  return data.exists ? data.app_user_id! : null;
}

async function registerBinding(hostUserId: string): Promise<string> {
  const res = await fetch('/api/user', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ host_user_id: hostUserId, action: 'register' }),
  });
  if (!res.ok) throw new Error(`/api/user register failed: ${res.status}`);
  const data = (await res.json()) as { app_user_id: string };
  return data.app_user_id;
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [status, setStatus] = useState<
    'loading' | 'ready' | 'unbound' | 'error'
  >('loading');
  const [error, setError] = useState<string | null>(null);
  const [hostUserId, setHostUserId] = useState<string | null>(null);

  useEffect(() => {
    if (!isInIframe()) {
      console.warn('[decks] not in iframe, entering error state');
      setStatus('error');
      setError('not_in_iframe');
      return;
    }

    let cancelled = false;

    async function init() {
      try {
        console.log('[decks] requesting host_user_id via postMessage...');
        const hostId = await getHostUserId();
        if (cancelled) return;
        console.log('[decks] got host_user_id:', hostId.slice(0, 8) + '...');
        setHostUserId(hostId);

        const existing = await checkBinding(hostId);
        if (cancelled) return;

        if (existing) {
          console.log('[decks] user bound:', existing);
          setUser({ userId: existing, isNew: false });
          setStatus('ready');
          return;
        }

        // 未绑定 → 自动 register（对用户透明，不显示注册按钮）
        console.log('[decks] auto-registering binding...');
        const appUserId = await registerBinding(hostId);
        if (cancelled) return;
        console.log('[decks] auto-registered:', appUserId);
        setUser({ userId: appUserId, isNew: true });
        setStatus('ready');
      } catch (err) {
        if (!cancelled) {
          console.error('[decks] init error:', err);
          setStatus('error');
          setError((err as Error).message);
        }
      }
    }

    void init();
    return () => {
      cancelled = true;
    };
  }, []);

  const register = async () => {
    if (!hostUserId) return;
    try {
      const appUserId = await registerBinding(hostUserId);
      setUser({ userId: appUserId, isNew: true });
      setStatus('ready');
    } catch (err) {
      setStatus('error');
      setError((err as Error).message);
    }
  };

  return (
    <UserContext.Provider value={{ user, status, error, register }}>
      {children}
    </UserContext.Provider>
  );
}

// ─── UI: gate / loading / register / error ───────────────

export function UserGate({ children }: { children: ReactNode }) {
  const { user, status, error } = useUser();

  if (status === 'loading') {
    return (
      <div className="page">
        <header className="page__header">
          <div className="skeleton skeleton--title" />
          <div className="skeleton skeleton--btn" />
        </header>
        <main className="page__body">
          <section className="section">
            <div className="section__head">
              <div className="skeleton skeleton--heading" />
              <div className="skeleton skeleton--sub" />
            </div>
            <div className="template-row">
              {Array.from({ length: 7 }).map((_, i) => (
                <div key={i} className="skeleton-tile">
                  <div className="skeleton skeleton--cover" />
                  <div className="skeleton-tile__meta">
                    <div className="skeleton skeleton--line skeleton--line-60" />
                    <div className="skeleton skeleton--line skeleton--line-80" />
                    <div className="skeleton skeleton--btn skeleton--btn-sm" />
                  </div>
                </div>
              ))}
            </div>
          </section>
          <section className="section">
            <div className="section__head">
              <div className="skeleton skeleton--heading" />
            </div>
            <div className="deck-grid">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="skeleton-tile">
                  <div className="skeleton skeleton--cover" />
                  <div className="skeleton-tile__meta">
                    <div className="skeleton skeleton--line skeleton--line-70" />
                    <div className="skeleton skeleton--line skeleton--line-40" />
                  </div>
                </div>
              ))}
            </div>
          </section>
        </main>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="center-card">
        <h2>请通过小北打开</h2>
        <p>
          这是小北 PPT 应用，需要从小北侧栏点「PPT」入口进入。
          {error ? <small style={{ display: 'block', marginTop: 8, color: 'var(--text-3)' }}>{error}</small> : null}
        </p>
      </div>
    );
  }

  if (status === 'unbound') {
    // 不应该出现在这个状态：init() 在未绑定时会自动 register
    // 留这个分支只是为了类型完整 + 极端 race 时的兜底
    return (
      <div className="center-card">
        <p>正在初始化…</p>
      </div>
    );
  }

  if (status === 'ready' && user) {
    return <>{children}</>;
  }

  return null;
}
